// Undertime is measured in hours on a single day, unlike leave which is
// measured in days across a range. Nothing here touches leave credits —
// the numbers exist so payroll can compute salary deductions.

export type UndertimeType = 'late_in' | 'early_out' | 'mid_shift' | 'other';

export const UNDERTIME_TYPE_LABELS: Record<UndertimeType, string> = {
  late_in: 'Late arrival',
  early_out: 'Early out',
  mid_shift: 'Left mid-shift',
  other: 'Other',
};

/** Hours between two times on the same day, rounded to 2 decimals. */
export function calculateUndertimeHours(from: Date | null, to: Date | null): number {
  if (!from || !to) return 0;
  const minutes =
    (to.getHours() * 60 + to.getMinutes()) - (from.getHours() * 60 + from.getMinutes());
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Math.round((minutes / 60) * 100) / 100;
}

/** 'HH:mm:ss' (as stored in Postgres `time`) → a Date on an arbitrary day. */
export function parseTimeString(value?: string | null): Date | null {
  if (!value) return null;
  const [h, m] = value.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

/** Date → 'HH:mm:ss' for a Postgres `time` column. */
export function formatTimeString(value: Date | null): string | null {
  if (!value) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(value.getHours())}:${pad(value.getMinutes())}:00`;
}

/** '13:45:00' → '1:45 PM' for display. */
export function displayTime(value?: string | null): string {
  const d = parseTimeString(value);
  if (!d) return '—';
  const h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "2.5" → "2h 30m" — how payroll reads it. */
export function formatHours(hours?: number | null): string {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0h';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
